import type { ToOriginalImageUrl } from './mediaContracts';
import { toSafeHttpUrl } from './safeUrl';

type QualityPref = 'high' | 'medium' | 'low';

/** Twitter/X pbs image size tokens. Grid uses small; downloads use orig. */
export type PbsImageSize = 'thumb' | 'small' | 'medium' | 'large' | 'orig';

/**
 * Rewrite a pbs.twimg.com URL to a specific size (`name=` / legacy `:size` suffix).
 * Non-pbs URLs are returned unchanged.
 */
export const toPbsImageUrl = (url: string, size: PbsImageSize): string => {
  if (!url || !url.includes('pbs.twimg.com')) return url;
  try {
    const parsed = new URL(url);
    // Legacy suffix style: .../media/abc.jpg:large
    const suffixMatch = parsed.pathname.match(/^(.*\.(?:jpg|jpeg|png|webp)):(\w+)$/i);
    if (suffixMatch) {
      parsed.pathname = `${suffixMatch[1]}:${size}`;
      return parsed.toString();
    }
    // Query-param style: ?format=jpg&name=small
    const extMatch = parsed.pathname.match(/\.(jpg|jpeg|png|webp)$/i);
    if (extMatch && !parsed.searchParams.get('format')) {
      parsed.pathname = parsed.pathname.replace(/\.(jpg|jpeg|png|webp)$/i, '');
      parsed.searchParams.set('format', extMatch[1].toLowerCase());
    }
    parsed.searchParams.set('name', size);
    return parsed.toString();
  } catch {
    if (/name=[^&]*/.test(url)) {
      return url.replace(/name=[^&]*/, `name=${size}`);
    }
    return `${url}${url.includes('?') ? '&' : '?'}name=${size}`;
  }
};

/**
 * Force full-resolution Twitter/X image URLs.
 * pbs.twimg.com serves originals when `name=orig` is set (or `:orig` suffix on legacy URLs).
 */
export const toOriginalImageUrl: ToOriginalImageUrl = (url) =>
  toPbsImageUrl(url, 'orig');

/** Lightweight preview for gallery grid tiles (~680px max). */
export const toThumbnailImageUrl = (url: string): string =>
  toPbsImageUrl(url, 'small');

/** Lightbox / video poster — sharper than grid, still far lighter than `orig`. */
export const toPosterImageUrl = (url: string): string =>
  toPbsImageUrl(url, 'medium');

/**
 * In-viewer photo size — sharp enough for screens, far lighter than `orig`.
 * Touch UIs get `medium` (~1200px) for faster swipe decoding.
 */
export const toViewerImageUrl = (url: string, touch = false): string =>
  toPbsImageUrl(url, touch ? 'medium' : 'large');

export const getImageExtension = (url: string): string => {
  const match = url.match(/\.(jpe?g|png|webp|gif)(?:\?|$)/i);
  return match ? match[1].toLowerCase().replace('jpeg', 'jpg') : 'jpg';
};

export const sanitizeForFilename = (text: string): string =>
  text
    .trim()
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();

/** Filename used by the single-item downloader (custom name or tweet slug). */
export const buildDownloaderFilename = (opts: {
  customFilename?: string;
  username: string;
  title: string;
  extension: string;
  mediaSuffix?: string;
}): string => {
  if (opts.customFilename?.trim()) {
    return `${sanitizeForFilename(opts.customFilename)}.${opts.extension}`;
  }
  const shortTitle = sanitizeForFilename(opts.title.substring(0, 15));
  const suffix = opts.mediaSuffix || '';
  const base = [opts.username, shortTitle].filter(Boolean).join('_');
  return `${base}${suffix}.${opts.extension}`;
};

/**
 * Pick the best MP4 URL from a variants list for a quality preference.
 */
export const getBestMp4Url = (
  variants: Array<{ url: string; bitrate?: number }> | undefined,
  fallbackUrl: string,
  quality: QualityPref = 'high'
): string => {
  const list = variants || [{ url: fallbackUrl }];
  const mp4Variants = list
    .filter((v) => v.url && v.url.includes('.mp4'))
    .sort((a, b) => {
      if (a.bitrate != null && b.bitrate != null) {
        return (b.bitrate || 0) - (a.bitrate || 0);
      }
      const getQuality = (u: string) => {
        const m = u.match(/\/(\d+)x(\d+)\//);
        return m ? parseInt(m[2], 10) : 0;
      };
      return getQuality(b.url || '') - getQuality(a.url || '');
    });

  let selected;
  switch (quality) {
    case 'medium':
      selected = mp4Variants[Math.floor(mp4Variants.length / 2)];
      break;
    case 'low':
      selected = mp4Variants[mp4Variants.length - 1];
      break;
    default:
      selected = mp4Variants[0];
  }

  let url = selected?.url || fallbackUrl;
  if (url.startsWith('http:')) url = url.replace('http:', 'https:');
  return url;
};

const STATUS_URL_RE =
  /(?:https?:\/\/)?(?:www\.)?(?:twitter\.com|x\.com)\/(\w+)\/status\/(\d+)/gi;

/** Extract unique canonical status URLs from freeform paste text. */
export function parseXPostUrls(raw: string): string[] {
  const seen = new Set<string>();
  const urls: string[] = [];
  const text = raw.trim();
  if (!text) return urls;

  let match: RegExpExecArray | null;
  STATUS_URL_RE.lastIndex = 0;
  while ((match = STATUS_URL_RE.exec(text)) !== null) {
    const username = match[1];
    const id = match[2];
    if (seen.has(id)) continue;
    seen.add(id);
    urls.push(`https://x.com/${username}/status/${id}`);
  }

  return urls;
}

/**
 * Normalize user input to a bare handle.
 * Accepts "@handle", "handle", or profile URLs like "https://x.com/handle".
 * Returns null when nothing resembling a valid handle can be extracted.
 */
export const parseProfileHandle = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Reject status URLs — those are posts, not profiles
  if (/\/status\/\d+/i.test(trimmed)) return null;

  let candidate = trimmed;
  const urlMatch = trimmed.match(
    /(?:twitter\.com|x\.com)\/(@?\w{1,15})(?:\/(?:media|with_replies|likes))?\/?(?:[?#]|$)/i
  );
  if (urlMatch) {
    candidate = urlMatch[1];
  }

  candidate = candidate.replace(/^@/, '');
  if (/^\w{1,15}$/.test(candidate) && !/^(status|home|search|explore|i)$/i.test(candidate)) {
    return candidate;
  }
  return null;
};

/** Canonical x.com status URL from item fields (never relative / empty). */
export const resolveTweetUrl = (item: {
  tweetUrl?: string;
  tweetId?: string;
  author?: string;
}): string => {
  const id = String(item.tweetId || '').trim();
  const handle = (item.author || '').replace(/^@/, '').trim();
  if (id && handle) return `https://x.com/${handle}/status/${id}`;
  if (id) return `https://x.com/i/status/${id}`;

  return toSafeHttpUrl(item.tweetUrl) || '';
};
